/**
 * How a finished effort reaches the summary route.
 *
 * The summary is a full-viewport surface, and F-NAV-23 forbids reading state
 * back out of the URL — a refresh on `/train/summary` must not resurrect a
 * session. So the route cannot look its subject up from the address bar, and it
 * is handed one instead: a host (the live workout, open effort, manual entry, or
 * History's Details action) calls {@link SummaryPresentationState.present} with
 * the archived record and the ports it should read, and the shell mounts the
 * route because the same call sets the full-viewport path.
 *
 * The ports arrive in the request rather than from a context of their own
 * because the storage ports are not part of the app's `PortBundle` yet: the
 * host that archived the session already holds them, and passing them in keeps
 * this feature from constructing a second store of its own.
 */

import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { EffortSessionFields, SessionPlan } from "@ff/domain";
import type { ReflectionStorePort } from "@ff/domain/ports";

import { useNavigation } from "../../app-state/navigation";
import type { RecapOpener } from "./recap";

/** The route's path in `shell/routeTable.ts`. Presented, never navigated to. */
export const SUMMARY_ROUTE_PATH = "/train/summary";

export interface SummaryPresentation {
  readonly session: EffortSessionFields;
  readonly reflections: ReflectionStorePort;
  /** F-HS-20: resolved by the host through `hasJournalRecap` before presenting. */
  readonly hasRecap: boolean;
  readonly openRecap: RecapOpener;
  /** Absent when the host cannot start a new workout from this record. */
  readonly onRepeat?: (plan: SessionPlan) => void;
  /** Runs after the overlay is dismissed and after any dirty reflection is saved. */
  readonly onDone?: () => void;
}

export interface SummaryPresentationState {
  readonly presentation: SummaryPresentation | null;
  readonly present: (presentation: SummaryPresentation) => void;
  readonly dismiss: () => void;
}

const SummaryPresentationContext = createContext<SummaryPresentationState | null>(null);

export function SummaryPresentationProvider({
  children,
}: {
  readonly children?: ReactNode;
}): ReactNode {
  const navigation = useNavigation();
  const [presentation, setPresentation] = useState<SummaryPresentation | null>(null);

  const { presentFullViewport, dismissFullViewport } = navigation;

  const present = useCallback(
    (next: SummaryPresentation) => {
      // The record is stored before the path is set: the shell mounts the route
      // synchronously from the path, and a route that mounted first would render
      // its empty state for one commit.
      setPresentation(next);
      presentFullViewport(SUMMARY_ROUTE_PATH);
    },
    [presentFullViewport],
  );

  const dismiss = useCallback(() => {
    dismissFullViewport();
    setPresentation(null);
  }, [dismissFullViewport]);

  const value = useMemo<SummaryPresentationState>(
    () => ({ presentation, present, dismiss }),
    [dismiss, present, presentation],
  );

  return (
    <SummaryPresentationContext.Provider value={value}>
      {children}
    </SummaryPresentationContext.Provider>
  );
}

/**
 * Throws rather than returning a lazily built default: a second, silently
 * created presentation store would let a host present a summary that the route
 * never sees, and the failure would look like a missing session rather than a
 * missing provider.
 */
export function useSummaryPresentation(): SummaryPresentationState {
  const state = useContext(SummaryPresentationContext);
  if (state === null) {
    throw new Error(
      "useSummaryPresentation() requires an enclosing <SummaryPresentationProvider>. " +
        "Place one above the surfaces that finish an effort and above the route table.",
    );
  }
  return state;
}

/** For the route itself, which must render harmlessly outside a provider. */
export function useOptionalSummaryPresentation(): SummaryPresentationState | null {
  return useContext(SummaryPresentationContext);
}
